/**
 * Prentus Pricing Calculator v2: pure, typed pricing logic.
 *
 * Source of truth: calculator-v2-spec.md (Rod-approved, 2026-09-24). Every
 * price and rule below is transcribed from that spec. Fields the spec marks
 * DRAFT are flagged with `draft: true` so the UI can show a "Draft" marker;
 * they are editable in the internal view but never silently hidden.
 *
 * Payment mechanic note: Rod set the current partner-portal calculator
 * (`license.ts`/`enrollment.ts` on main) as the baseline: the full contract,
 * onboarding included, is billed at signature for every term, plus a 90-day
 * opt-out line. pricing-v2 mirrors that mechanic exactly (see
 * computePaymentSchedule). There is no day-90 split for 1-year terms.
 *
 * All money values are kept as raw numbers (no in-module rounding beyond
 * what the spec explicitly calls for, e.g. `round(enrollment x activationRate)`)
 * so formatting stays the caller's responsibility, matching the existing
 * calculators' convention (see ../lib/format.ts).
 */

import type { PaymentLine, TermYears } from './enrollment'
import type { CustomerSegment, PricingModesSetting } from '../types'

export type { TermYears }

export type PricingMode = 'license' | 'unlimited'
/**
 * How the term's platform/subscription fee is paid (Rod, 2026-09-27). Only meaningful for 2- and
 * 3-year terms: a 1-year term is always upfront regardless of this flag, since there is only ever
 * one year to pay for. 'upfront' (the default, and the only behavior before this flag existed)
 * pays the full term at signature and gets the lower rate the pooled multi-year quantity earns.
 * 'yearly' pays one year at a time — at signature, then on each anniversary — and is priced at the
 * tier the ONE-YEAR quantity earns (never the pooled multi-year tier), even though licenses still
 * pool across the full term either way.
 */
export type PaymentFrequency = 'upfront' | 'yearly'
export type UnlimitedMethod = 'formula' | 'band'
export type CustomerType = 'university' | 'career_program'
export type ModuleId = 'ai_coaching' | 'employer_partnerships' | 'outcomes_pro'
export type SupportTier = 'standard' | 'professional' | 'enterprise'
/** Why Enterprise support was auto-applied and cannot be downgraded; null when the requested tier stands. */
export type SupportForcedReason = 'separate_instance' | 'custom_integration' | null
export type IntegrationId = 'sis' | 'lms' | 'crm'
export type OnboardingModelId = 'current' | 'option1' | 'option2'
export type OnboardingScope = 1 | 1.5 | 2
export type Option2Package = 'standard' | 'guided' | 'enterprise'
export type FullPlatformDiscountBase = 'subscription' | 'modules'

// ---------------------------------------------------------------------------
// Config shape
// ---------------------------------------------------------------------------

export interface PricingTier {
  min: number
  max: number
  label: string
  price: number
}

export interface ModuleDef {
  id: ModuleId
  name: string
  description: string
  price: number
  /** Short bullet items rendered as a checklist under the description. */
  includes: string[]
}

export interface IntegrationDef {
  id: IntegrationId
  name: string
  /** Recurring, per license per year. */
  recurringPerLicense: number
  /** One-time onboarding build fee, used by Onboarding Option 1. Never waived. */
  onboardingFee: number
  description: string
  draft: boolean
}

export interface StarterDef {
  id: 'program_launch' | 'ai_coaching_starter' | 'outcomes_starter'
  name: string
  annual: number
  limit: number | null
  limitLabel: string | null
  draft: boolean
  note: string
}

export interface ValueBand {
  max: number
  fee: number
  label: string
  custom?: boolean
}

export interface MeteredScanType {
  id: 'url_scan' | 'search_scan'
  name: string
  /** Scan credits (from Outcomes Pro) consumed per verified result of this type. */
  credits: number
  standardRate: number
  proRate: number
}

export interface Option2PackageDef {
  price: number
  /** 0 means no discount at that term. */
  twoYearDiscount: number
  threeYearDiscount: number
  threeYearWaived: boolean
}

export interface IncludedFeatureDef {
  name: string
  definition: string
  beta?: boolean
  /** Kept in config for later use but filtered out of rendering, e.g. while a feature is not yet priced. */
  hidden?: boolean
}

export interface PricingV2Config {
  version: string
  licenseMinimum: number
  tiers: PricingTier[]
  careerPlatformIncludedFeatures: IncludedFeatureDef[]
  modules: ModuleDef[]
  automatedWorkflows: {
    pricePerLicense: number
  }
  fullPlatform: {
    requiredModules: ModuleId[]
    discountPercent: number
    discountBase: FullPlatformDiscountBase
    /** Scan credits per license per year while Full Platform is active. */
    scanCreditsPerLicense: number
  }
  outcomesProCredits: {
    /** Scan credits per license per year, Outcomes Pro selected alone. */
    standardPerLicense: number
    /** Scan credits per license per year, Full Platform active. */
    fullPlatformPerLicense: number
  }
  support: {
    standard: {
      hoursPerQuarter: number
      includes: string[]
    }
    professional: {
      perLicensePerYear: number
      hoursPerQuarter: number
      includes: string[]
    }
    enterprise: {
      /** Fraction of the "subscription" base (see enterpriseSupportBase). */
      percentOfSubscription: number
      annualMinimum: number
      hoursPerQuarter: number
      includes: string[]
    }
    /** Applies to every tier once its included hours are used up. */
    overagePerHour: number
    overageReportingCadence: string
  }
  integrations: IntegrationDef[]
  enterpriseItems: {
    separateInstanceAnnual: number
  }
  starters: StarterDef[]
  metered: {
    /** Per-verified-result outcomes work, billed outside the subscription. */
    scanTypes: MeteredScanType[]
    /** Metered items not yet priced; shown as "Coming soon". */
    comingSoon: string[]
  }
  unlimited: {
    activationRateDefault: number
    bands: ValueBand[]
    /**
     * Enrollment banding for the Unlimited formula method (round 9, 2026-09-28): priced
     * enrollment always rounds UP to the top of its band, so 9,999 and 10,000 price the same.
     * `upTo: null` on the last entry means "no ceiling" (kept JSON-safe; never Infinity).
     * Regimes apply in order and are exclusive of the previous entry's `upTo`, e.g. with
     * `[{ upTo: 3000, step: 500 }, { upTo: null, step: 1000 }]`: 1-500, 501-1,000, ...,
     * 2,501-3,000 step by 500, then 3,001-4,000, 4,001-5,000, ... step by 1,000.
     */
    enrollmentSteps: { upTo: number | null; step: number }[]
  }
  onboarding: {
    /** Internal-view-only comparison; never drives the customer-facing quote. */
    current: { floor: number; rate: number; twoYearDiscount: number; threeYearWaived: boolean }
    /** The only customer-facing onboarding model: base setup plus add-ons. */
    option1: {
      baseSetup: number
      twoYearDiscount: number
      threeYearWaived: boolean
      migrationFee: number
      /** DRAFT: additional training session, priced per session (quantity input). */
      additionalTrainingFee: number
    }
    /** Internal-view-only comparison; never drives the customer-facing quote. */
    option2: {
      standard: Option2PackageDef
      guided: Option2PackageDef
      enterprise: Option2PackageDef
    }
  }
  switchingCredit: {
    capPercent: number
    draft: boolean
  }
}

/**
 * Tags calculator_inputs saved by the live workspace ROI & Pricing tab (and
 * validated by the Worker's quote route) as v2-shaped, distinguishing them
 * from the pre-v2 license.ts/enrollment.ts input shape stored under the same
 * jsonb column. Bump this only when the v2 input shape itself changes in a
 * way that needs its own adapter branch; it does not need to track every
 * pricing-formula tweak.
 */
export const PRICING_V2_INPUT_VERSION = 'pricing-v2-2026-09-25'

export const PRICING_V2: PricingV2Config = {
  version: 'pricing-v2-2026-09-28',
  licenseMinimum: 500,
  tiers: [
    { min: 500, max: 999, label: '500-999', price: 25 },
    { min: 1_000, max: 2_499, label: '1,000-2,499', price: 23 },
    { min: 2_500, max: 4_999, label: '2,500-4,999', price: 20 },
    { min: 5_000, max: 9_999, label: '5,000-9,999', price: 18 },
    { min: 10_000, max: Number.POSITIVE_INFINITY, label: '10,000+', price: 16 },
  ],
  careerPlatformIncludedFeatures: [
    { name: 'Community', definition: 'A private space for students, alumni, and staff to post and join groups.' },
    { name: 'Job board with daily matches', definition: 'Your own job board with partner jobs, plus daily matches for every student.' },
    { name: 'Job search tools', definition: 'A job tracker, Chrome extension, and resume, LinkedIn, and cover letter builders with AI reviews.' },
    { name: 'Advisor Suite', definition: 'Dashboards, activity logs, a coach review queue, and cohort messaging.' },
    { name: 'Outcome tracking', definition: 'Automatic tracking of every job seeker, email surveys, and an outcomes dashboard.' },
    { name: 'Data APIs', definition: 'Self-serve APIs to pull student, job, and outcomes data into your systems.' },
    { name: 'Custom domain', definition: 'Runs on your own web address.' },
    { name: 'SSO', definition: "Students and staff sign in with your school's login." },
    { name: 'Career Exploration', definition: 'Helps undecided students explore careers and programs with live job data. Free until priced, then a paid\u00a0add-on.', beta: true, hidden: true },
  ],
  modules: [
    {
      id: 'ai_coaching',
      name: 'AI Coaching',
      description: 'AI career advice and mock interviews for every student, any time.',
      price: 5,
      includes: [
        'A 24/7 AI career advisor that answers routine questions and gives personal career advice, so advisors can focus on the students who need them most',
        'Voice AI mock interviews built from real job descriptions, so students practice the interview they will actually face',
        'Feedback after every mock interview, so students sharpen their answers before interview day',
        'Unlimited mock interviews for every covered student',
      ],
    },
    {
      id: 'employer_partnerships',
      name: 'Employer Partnerships',
      description: 'Grow and manage your employer network in one place.',
      price: 2,
      includes: [
        'An employer CRM to track every employer relationship and contact',
        'Talent Links to share candidates and grow your network without employers creating an account',
        'Employer portals and job posting, so partners post roles straight to your job board',
        'Bring your existing employer network into Prentus and manage it there',
      ],
    },
    {
      id: 'outcomes_pro',
      name: 'Outcomes Pro',
      description: 'Per-student outcomes, compliance reports, and half-price scans.',
      price: 3,
      includes: [
        'Per-student results and exports from automatic tracking of every job seeker',
        'Compliance-ready reports to share with accreditors and funders',
        'Labor-market and skills trends that show which roles and skills employers want',
        '2 scan credits per license, 3 with Full Platform, then 50% off every scan after that',
      ],
    },
  ],
  automatedWorkflows: {
    pricePerLicense: 3,
  },
  fullPlatform: {
    requiredModules: ['ai_coaching', 'employer_partnerships', 'outcomes_pro'],
    discountPercent: 0.10,
    discountBase: 'subscription',
    scanCreditsPerLicense: 3,
  },
  outcomesProCredits: {
    standardPerLicense: 2,
    fullPlatformPerLicense: 3,
  },
  support: {
    standard: {
      hoursPerQuarter: 5,
      includes: [
        'A 24/7 AI support agent for how-to questions, any time',
        '5 hours a quarter with the Prentus team, including your onboarding',
        'Help center and step-by-step guides',
      ],
    },
    professional: {
      perLicensePerYear: 2,
      hoursPerQuarter: 10,
      includes: [
        'Everything in Standard, with 10 hours a quarter',
        'A named customer success manager',
        'A success plan built around your goals',
        "Regular usage reviews that show what's working and where students drop off",
      ],
    },
    enterprise: {
      percentOfSubscription: 0.18,
      annualMinimum: 15_000,
      hoursPerQuarter: 25,
      includes: [
        'Everything in Professional, with 25 hours a quarter',
        'A dedicated, named support team',
        'Guaranteed response times (SLA)',
        'Custom work scoped through a statement of work',
      ],
    },
    overagePerHour: 225,
    overageReportingCadence: 'monthly',
  },
  integrations: [
    { id: 'sis', name: 'SIS', recurringPerLicense: 2, onboardingFee: 7_500, description: 'Keep student records in sync with your SIS.', draft: false },
    { id: 'lms', name: 'LMS', recurringPerLicense: 2, onboardingFee: 5_000, description: 'Keep courses and enrollments in sync with your LMS.', draft: false },
    { id: 'crm', name: 'CRM / Salesforce', recurringPerLicense: 2, onboardingFee: 7_500, description: 'Keep contacts and activity in sync with Salesforce or your CRM.', draft: false },
  ],
  enterpriseItems: {
    separateInstanceAnnual: 30_000,
  },
  starters: [
    {
      id: 'program_launch',
      name: 'Program Launch',
      annual: 7_500,
      limit: 250,
      limitLabel: 'up to 250 learners',
      draft: false,
      note: 'Career Platform for one program. Beyond 250 learners this moves to license pricing.',
    },
    {
      id: 'ai_coaching_starter',
      name: 'AI Coaching Starter',
      annual: 7_500,
      limit: 500,
      limitLabel: 'up to 500 students',
      draft: true,
      note: 'AI career agent and mock interviews for a single cohort.',
    },
    {
      id: 'outcomes_starter',
      name: 'Outcomes Starter',
      annual: 0,
      limit: null,
      limitLabel: null,
      draft: false,
      note: 'Free outcome tracking shown as totals, email surveys, and scans paid with credits.',
    },
  ],
  metered: {
    scanTypes: [
      { id: 'url_scan', name: 'LinkedIn scan, URL known', credits: 1, standardRate: 1, proRate: 0.5 },
      { id: 'search_scan', name: 'LinkedIn scan, search by name, email, and school', credits: 3, standardRate: 3, proRate: 1.5 },
    ],
    comingSoon: ['AI SMS and phone outcome verification'],
  },
  unlimited: {
    activationRateDefault: 0.25,
    enrollmentSteps: [
      { upTo: 3_000, step: 500 },
      { upTo: null, step: 1_000 },
    ],
    bands: [
      { max: 1_000, fee: 15_000, label: 'Up to 1,000 enrolled' },
      { max: 2_500, fee: 30_000, label: 'Up to 2,500 enrolled' },
      { max: 5_000, fee: 50_000, label: 'Up to 5,000 enrolled' },
      { max: 10_000, fee: 100_000, label: 'Up to 10,000 enrolled' },
      { max: 15_000, fee: 150_000, label: 'Up to 15,000 enrolled' },
      { max: 20_000, fee: 200_000, label: 'Up to 20,000 enrolled' },
      { max: 30_000, fee: 300_000, label: 'Up to 30,000 enrolled' },
      { max: 40_000, fee: 400_000, label: 'Up to 40,000 enrolled' },
      { max: 50_000, fee: 500_000, label: 'Up to 50,000 enrolled' },
      { max: Number.POSITIVE_INFINITY, fee: 500_000, label: 'Above 50,000 enrolled', custom: true },
    ],
  },
  onboarding: {
    current: { floor: 5_000, rate: 0.20, twoYearDiscount: 0.5, threeYearWaived: true },
    option1: { baseSetup: 5_000, twoYearDiscount: 0.5, threeYearWaived: true, migrationFee: 5_000, additionalTrainingFee: 500 },
    option2: {
      standard: { price: 5_000, twoYearDiscount: 0.5, threeYearDiscount: 1, threeYearWaived: true },
      // Interpreted: the spec states a 3-year discount for Guided/Enterprise but is silent on
      // a 2-year rate. Since these packages bundle one-time integration/instance work (like
      // Option 1's "never waived" integration fees), we default their 2-year discount to 0
      // (full price) rather than assuming the Standard package's 50%. Switchable concern:
      // if Rod wants a 2-year discount here, add it to Option2PackageDef.twoYearDiscount.
      guided: { price: 15_000, twoYearDiscount: 0, threeYearDiscount: 0.25, threeYearWaived: false },
      enterprise: { price: 40_000, twoYearDiscount: 0, threeYearDiscount: 0.25, threeYearWaived: false },
    },
  },
  switchingCredit: {
    capPercent: 0.25,
    draft: true,
  },
}

/** IDs used to render a "Draft" marker in the internal view. Keep in sync with PRICING_V2. */
export const DRAFT_ITEM_IDS: string[] = [
  'starters.ai_coaching_starter',
  'switchingCredit',
  'onboarding.option1.additionalTrainingFee',
]

// ---------------------------------------------------------------------------
// Shared input/result types
// ---------------------------------------------------------------------------

export interface LicenseModeInput {
  annualLicenses: number
  termYears: TermYears
  selectedModules: ModuleId[]
  automatedWorkflows: boolean
  supportTier: SupportTier
  selectedIntegrations: IntegrationId[]
  separateInstance: boolean
  /** Custom connectors beyond the standard SIS/LMS/CRM. No recurring price; scoped in a statement of work. Forces Enterprise support. */
  customIntegrationBuild: boolean
  /** Only meaningful for 2- and 3-year terms; a 1-year term is always priced upfront regardless. Defaults to 'upfront'. */
  payment?: PaymentFrequency
}

export interface ModuleLineItem {
  id: ModuleId
  name: string
  price: number
  annual: number
}

export interface IntegrationLineItem {
  id: IntegrationId
  name: string
  recurringPerLicense: number
  annual: number
  draft: boolean
  /** True when this integration is the one bundled free with Automated Workflows (non-Full-Platform only). */
  includedWithWorkflows: boolean
}

export interface LicenseModeResult {
  enteredLicenses: number
  billedLicenses: number
  pooledLicenses: number
  /** The effective payment frequency actually applied: always 'upfront' for a 1-year term regardless of what was requested. */
  payment: PaymentFrequency
  tierPrice: number
  /** The tier price at this billed count pooled for 1 year only, used to show multi-year volume savings. */
  oneYearTierPrice: number
  /** The tier price the full pooled multi-year quantity would earn if paid upfront, regardless of which payment mode is actually applied. Equals `tierPrice` when `payment` is 'upfront'; used in 'yearly' mode to show what upfront payment would have saved. */
  pooledTierPrice: number
  pricingBand: string
  baseAnnual: number
  moduleLineItems: ModuleLineItem[]
  modulesAnnual: number
  fullPlatform: boolean
  fullPlatformDiscount: number
  workflowsAnnual: number
  /** True only when Full Platform includes Automated Workflows free ($0). */
  workflowsIncluded: boolean
  /** True when Automated Workflows was auto-applied because an integration was selected without Full Platform. */
  workflowsForcedByIntegration: boolean
  /** The integration bundled free with Automated Workflows, non-Full-Platform only; null otherwise. */
  workflowsIncludedIntegrationId: IntegrationId | null
  requestedSupportTier: SupportTier
  appliedSupportTier: SupportTier
  /** Why appliedSupportTier was forced to Enterprise; null when the requested tier stands. */
  supportForcedReason: SupportForcedReason
  supportAnnual: number
  supportIncluded: boolean
  /** The base Enterprise's 18% is applied to: base licenses + modules - Full Platform discount + paid Automated Workflows. Excludes integrations, separate instance, and support itself. */
  enterpriseSupportBase: number
  /** What Enterprise support would cost for this quote's numbers, regardless of which tier is actually applied. Used for the Enterprise option's price preview. */
  enterpriseSupportAnnual: number
  customIntegrationBuild: boolean
  integrationLineItems: IntegrationLineItem[]
  integrationsAnnual: number
  separateInstanceAnnual: number
  scanCreditsPerLicense: number
  scanCreditsPerYear: number
  annualSubscription: number
  onePricePerLicensePerYear: number
}

function findTier(config: PricingV2Config, pooledLicenses: number): PricingTier {
  return config.tiers.find((tier) => pooledLicenses >= tier.min && pooledLicenses <= tier.max) ?? config.tiers[0]
}

function isFullPlatform(config: PricingV2Config, selectedModules: ModuleId[]): boolean {
  return config.fullPlatform.requiredModules.every((id) => selectedModules.includes(id))
}

/** Separate instance or a custom integration build auto-applies Enterprise support; it cannot be downgraded. */
function resolveSupportForcedReason(separateInstance: boolean, customIntegrationBuild: boolean): SupportForcedReason {
  if (separateInstance) return 'separate_instance'
  if (customIntegrationBuild) return 'custom_integration'
  return null
}

/** 18% of the subscription base, with a $15,000 annual minimum. */
function computeEnterpriseSupportAnnual(config: PricingV2Config, subscriptionBase: number): number {
  return Math.max(config.support.enterprise.annualMinimum, subscriptionBase * config.support.enterprise.percentOfSubscription)
}

/**
 * Smallest billable licenses per year. The license minimum (500) is a per-year floor, except
 * on a 2- or 3-year term paid upfront, where it applies across the whole term: the per-year
 * floor is the minimum split over the term, rounded up (250 a year for 2 years, 167 for 3)
 * (Rod, 2026-10-08). Paying yearly keeps the full per-year minimum.
 */
export function minimumLicensesPerYear(termYears: TermYears, payment: PaymentFrequency | undefined, config: PricingV2Config = PRICING_V2): number {
  const upfront = (payment ?? 'upfront') === 'upfront'
  return termYears > 1 && upfront ? Math.ceil(config.licenseMinimum / termYears) : config.licenseMinimum
}

/**
 * Core "Per license" pricing engine (spec T1, T2, T6-T10). Also reused by the
 * Unlimited formula method, which prices `searchers` exactly like licenses.
 */
export function computeLicenseModeQuote(input: LicenseModeInput, config: PricingV2Config = PRICING_V2): LicenseModeResult {
  const enteredLicenses = Math.max(0, Math.floor(input.annualLicenses || 0))
  // A 1-year term is always upfront: there is only ever one year to pay for, so the payment
  // flag has no effect (see the PaymentFrequency doc comment).
  const payment: PaymentFrequency = input.termYears === 1 ? 'upfront' : (input.payment ?? 'upfront')
  const billedLicenses = enteredLicenses ? Math.max(minimumLicensesPerYear(input.termYears, payment, config), enteredLicenses) : 0
  const pooledLicenses = billedLicenses * input.termYears
  const pooledTier = findTier(config, pooledLicenses)
  const oneYearTier = findTier(config, billedLicenses)
  // Paying yearly prices at the one-year quantity's tier, never the pooled multi-year tier,
  // even though licenses still pool across the full term either way (Rod, 2026-09-27).
  const tier = payment === 'yearly' ? oneYearTier : pooledTier
  const baseAnnual = billedLicenses * tier.price

  const fullPlatform = isFullPlatform(config, input.selectedModules)
  const moduleLineItems: ModuleLineItem[] = config.modules
    .filter((module) => input.selectedModules.includes(module.id))
    .map((module) => ({ id: module.id, name: module.name, price: module.price, annual: billedLicenses * module.price }))
  const modulesAnnual = moduleLineItems.reduce((sum, item) => sum + item.annual, 0)

  const discountableAnnual = config.fullPlatform.discountBase === 'modules' ? modulesAnnual : baseAnnual + modulesAnnual
  const fullPlatformDiscount = fullPlatform ? discountableAnnual * config.fullPlatform.discountPercent : 0

  // Automated Workflows / service integration rule (spec 2026-09-25):
  // - Full Platform: Automated Workflows is included at $0; every selected integration is
  //   $2/license, no free first integration.
  // - Without Full Platform: Automated Workflows is $3/license. Selecting ANY integration
  //   auto-applies Automated Workflows and bundles the first selected integration free; each
  //   additional integration is $2/license. Automated Workflows can also be bought alone (no
  //   integrations selected) via the manual toggle, at the same $3/license.
  const hasIntegrations = input.selectedIntegrations.length > 0
  const workflowsForcedByIntegration = !fullPlatform && hasIntegrations
  const workflowsIncluded = fullPlatform
  const workflowsAnnual = fullPlatform
    ? 0
    : (workflowsForcedByIntegration || input.automatedWorkflows)
      ? billedLicenses * config.automatedWorkflows.pricePerLicense
      : 0
  const workflowsIncludedIntegrationId: IntegrationId | null =
    !fullPlatform && workflowsAnnual > 0 && hasIntegrations ? input.selectedIntegrations[0]! : null

  // "Subscription" for the Enterprise 18% base: base licenses + modules - Full Platform discount +
  // paid Automated Workflows. Excludes integrations, separate instance, and support itself.
  const enterpriseSupportBase = baseAnnual + modulesAnnual - fullPlatformDiscount + workflowsAnnual
  const enterpriseSupportAnnual = computeEnterpriseSupportAnnual(config, enterpriseSupportBase)

  const requestedSupportTier = input.supportTier
  const supportForcedReason = resolveSupportForcedReason(input.separateInstance, input.customIntegrationBuild)
  // Full Platform locks support to Professional (included free) unless the customer explicitly
  // picks Enterprise: Standard is not a valid selection while all three modules are active.
  const appliedSupportTier: SupportTier = supportForcedReason
    ? 'enterprise'
    : fullPlatform && requestedSupportTier !== 'enterprise'
      ? 'professional'
      : requestedSupportTier
  const supportIncluded = fullPlatform && appliedSupportTier === 'professional'
  const supportAnnual = supportIncluded
    ? 0
    : appliedSupportTier === 'professional'
      ? billedLicenses * config.support.professional.perLicensePerYear
      : appliedSupportTier === 'enterprise'
        ? enterpriseSupportAnnual
        : 0

  const integrationLineItems: IntegrationLineItem[] = config.integrations
    .filter((integration) => input.selectedIntegrations.includes(integration.id))
    .map((integration) => {
      const includedWithWorkflows = integration.id === workflowsIncludedIntegrationId
      return {
        id: integration.id,
        name: integration.name,
        recurringPerLicense: integration.recurringPerLicense,
        annual: includedWithWorkflows ? 0 : billedLicenses * integration.recurringPerLicense,
        draft: integration.draft,
        includedWithWorkflows,
      }
    })
  const integrationsAnnual = integrationLineItems.reduce((sum, item) => sum + item.annual, 0)

  const separateInstanceAnnual = input.separateInstance ? config.enterpriseItems.separateInstanceAnnual : 0

  const outcomesProSelected = input.selectedModules.includes('outcomes_pro')
  const scanCreditsPerLicense = fullPlatform
    ? config.outcomesProCredits.fullPlatformPerLicense
    : outcomesProSelected
      ? config.outcomesProCredits.standardPerLicense
      : 0
  const scanCreditsPerYear = scanCreditsPerLicense * billedLicenses

  const annualSubscription = baseAnnual + modulesAnnual - fullPlatformDiscount + workflowsAnnual + supportAnnual + integrationsAnnual + separateInstanceAnnual

  return {
    enteredLicenses,
    billedLicenses,
    pooledLicenses,
    payment,
    tierPrice: tier.price,
    oneYearTierPrice: oneYearTier.price,
    pooledTierPrice: pooledTier.price,
    pricingBand: tier.label,
    baseAnnual,
    moduleLineItems,
    modulesAnnual,
    fullPlatform,
    fullPlatformDiscount,
    workflowsAnnual,
    workflowsIncluded,
    workflowsForcedByIntegration,
    workflowsIncludedIntegrationId,
    requestedSupportTier,
    appliedSupportTier,
    supportForcedReason,
    supportAnnual,
    supportIncluded,
    enterpriseSupportBase,
    enterpriseSupportAnnual,
    customIntegrationBuild: input.customIntegrationBuild,
    integrationLineItems,
    integrationsAnnual,
    separateInstanceAnnual,
    scanCreditsPerLicense,
    scanCreditsPerYear,
    annualSubscription,
    onePricePerLicensePerYear: billedLicenses > 0 ? annualSubscription / billedLicenses : 0,
  }
}

// ---------------------------------------------------------------------------
// Itemized price breakdown, for the summary's "Price breakdown" section
// ---------------------------------------------------------------------------

export interface BreakdownLine {
  label: string
  /** 0 for 'included' and 'note' lines (not charged); negative for discount/credit lines. */
  amount: number
  kind: 'charge' | 'discount' | 'included' | 'subtotal' | 'note'
  /** For 'included' lines: what this would cost if it weren't included, shown muted. */
  includedValue?: number
  detail?: string
}

/**
 * Decomposes an already-computed license/unlimited-formula quote into
 * itemized lines that sum exactly to `annualSubscription`. Order matches the
 * spec: Career Platform (+ multi-year volume discount when applicable), each
 * module, Automated Workflows, the Full Platform discount, service
 * integrations, separate instance, support, then the annual subscription
 * subtotal.
 */
export function computeSubscriptionBreakdown(quote: LicenseModeResult, config: PricingV2Config = PRICING_V2): BreakdownLine[] {
  const lines: BreakdownLine[] = []
  const workflowsWouldBeAnnual = quote.billedLicenses * config.automatedWorkflows.pricePerLicense
  const professionalWouldBeAnnual = quote.billedLicenses * config.support.professional.perLicensePerYear
  const includedIntegration = quote.integrationLineItems.find((item) => item.includedWithWorkflows)

  if (quote.oneYearTierPrice !== quote.tierPrice) {
    const atOneYearRate = quote.billedLicenses * quote.oneYearTierPrice
    const volumeSavings = atOneYearRate - quote.baseAnnual
    lines.push({ label: 'Career Platform', amount: atOneYearRate, kind: 'charge', detail: `${quote.billedLicenses.toLocaleString('en-US')} licenses x $${quote.oneYearTierPrice}` })
    lines.push({ label: 'Multi-year volume pricing', amount: -volumeSavings, kind: 'discount' })
  } else {
    lines.push({ label: 'Career Platform', amount: quote.baseAnnual, kind: 'charge', detail: `${quote.billedLicenses.toLocaleString('en-US')} licenses x $${quote.tierPrice}` })
  }

  // Paying yearly means the customer never sees the pooled multi-year discount (tier is picked by
  // one year's quantity instead). Surface what the lower upfront rate would have been, but only
  // when it would actually have been lower — no negative framing when there is nothing to gain.
  if (quote.payment === 'yearly' && quote.pooledTierPrice < quote.tierPrice) {
    lines.push({ label: `Multi-year rate with upfront payment: $${quote.pooledTierPrice} a year`, amount: 0, kind: 'note' })
  }

  for (const module of quote.moduleLineItems) {
    lines.push({ label: module.name, amount: module.annual, kind: 'charge', detail: `${quote.billedLicenses.toLocaleString('en-US')} licenses x $${module.price}` })
  }

  if (quote.workflowsIncluded) {
    lines.push({ label: 'Automated Workflows', amount: 0, kind: 'included', includedValue: workflowsWouldBeAnnual })
  } else if (quote.workflowsAnnual > 0) {
    lines.push({
      label: 'Automated Workflows',
      amount: quote.workflowsAnnual,
      kind: 'charge',
      detail: includedIntegration
        ? `Includes your first integration (${includedIntegration.name})`
        : `${quote.billedLicenses.toLocaleString('en-US')} licenses x $${config.automatedWorkflows.pricePerLicense}`,
    })
  }

  if (quote.fullPlatform && quote.fullPlatformDiscount > 0) {
    lines.push({ label: 'Full Platform discount', amount: -quote.fullPlatformDiscount, kind: 'discount' })
  }

  for (const integration of quote.integrationLineItems) {
    // The integration bundled free with Automated Workflows is folded into that line above.
    if (integration.includedWithWorkflows) continue
    lines.push({
      label: `${integration.name} integration`,
      amount: integration.annual,
      kind: 'charge',
      detail: `${quote.billedLicenses.toLocaleString('en-US')} licenses x $${integration.recurringPerLicense}`,
    })
  }

  if (quote.separateInstanceAnnual > 0) {
    lines.push({ label: 'Separate instance', amount: quote.separateInstanceAnnual, kind: 'charge' })
  }

  if (quote.customIntegrationBuild) {
    lines.push({ label: 'Custom integration build, scoped separately in a statement of work', amount: 0, kind: 'note' })
  }

  if (quote.supportIncluded) {
    lines.push({ label: 'Professional support', amount: 0, kind: 'included', includedValue: professionalWouldBeAnnual })
  } else if (quote.appliedSupportTier === 'professional') {
    lines.push({ label: 'Professional support', amount: quote.supportAnnual, kind: 'charge' })
  } else if (quote.appliedSupportTier === 'enterprise') {
    const reason = quote.supportForcedReason === 'separate_instance'
      ? 'Required with a separate instance'
      : quote.supportForcedReason === 'custom_integration'
        ? 'Required with a custom integration build'
        : undefined
    lines.push({ label: 'Enterprise support', amount: quote.supportAnnual, kind: 'charge', detail: reason })
  }

  lines.push({ label: 'Annual subscription', amount: quote.annualSubscription, kind: 'subtotal' })

  return lines
}

// ---------------------------------------------------------------------------
// Unlimited: formula method
// ---------------------------------------------------------------------------

export interface UnlimitedFormulaInput {
  enrollment: number
  activationRate: number
  termYears: TermYears
  selectedModules: ModuleId[]
  automatedWorkflows: boolean
  supportTier: SupportTier
  selectedIntegrations: IntegrationId[]
  separateInstance: boolean
  customIntegrationBuild: boolean
  /** Only meaningful for 2- and 3-year terms; a 1-year term is always priced upfront regardless. Defaults to 'upfront'. */
  payment?: PaymentFrequency
}

export interface UnlimitedFormulaResult extends LicenseModeResult {
  enrollment: number
  activationRate: number
  searchers: number
  onePricePerEnrolledStudentPerYear: number
  /** Enrollment rounded up to the top of its band (round 9, 2026-09-28); what the formula actually prices. */
  pricedEnrollment: number
  /** The band `pricedEnrollment` falls in, e.g. { min: 2_001, max: 2_500 }. */
  enrollmentBand: { min: number; max: number }
}

/**
 * Rounds enrollment up to the top of its band (round 9, 2026-09-28): up to 3,000 enrolled steps
 * by 500 (1-500, 501-1,000, ...), above 3,000 steps by 1,000 (3,001-4,000, 4,001-5,000, ...), per
 * `config.unlimited.enrollmentSteps`. So 9,999 and 10,000 both land on the 9,001-10,000 band and
 * price the same. Enrollment of 0 or less returns a zeroed band ({ min: 0, max: 0 }).
 */
export function unlimitedEnrollmentBand(enrollment: number, config: PricingV2Config = PRICING_V2): { min: number; max: number } {
  const safeEnrollment = Math.max(0, Math.floor(enrollment || 0))
  if (safeEnrollment === 0) return { min: 0, max: 0 }

  let regimeStart = 0
  for (const { upTo, step } of config.unlimited.enrollmentSteps) {
    const regimeEnd = upTo ?? Number.POSITIVE_INFINITY
    if (safeEnrollment <= regimeEnd) {
      const stepsFromRegimeStart = Math.ceil((safeEnrollment - regimeStart) / step)
      const max = regimeStart + stepsFromRegimeStart * step
      return { min: max - step + 1, max }
    }
    regimeStart = regimeEnd
  }
  // Unreachable while the last entry's `upTo` is null (no ceiling), kept as a safe fallback.
  const lastStep = config.unlimited.enrollmentSteps.at(-1)?.step ?? 1
  const stepsFromRegimeStart = Math.ceil((safeEnrollment - regimeStart) / lastStep)
  const max = regimeStart + stepsFromRegimeStart * lastStep
  return { min: max - lastStep + 1, max }
}

/**
 * Spec: enrollment is priced by band (unlimitedEnrollmentBand), always at the band's top, then
 * searchers = max(500, round(pricedEnrollment x activationRate)), priced exactly like licenses.
 * `onePricePerEnrolledStudentPerYear` still divides by the ACTUAL entered enrollment, not the
 * banded/priced figure, so that per-student figure reflects what the customer typed.
 */
export function computeUnlimitedFormulaQuote(input: UnlimitedFormulaInput, config: PricingV2Config = PRICING_V2): UnlimitedFormulaResult {
  const enrollmentBand = unlimitedEnrollmentBand(input.enrollment, config)
  const pricedEnrollment = enrollmentBand.max
  const searchers = Math.max(config.licenseMinimum, Math.round(pricedEnrollment * input.activationRate))
  const base = computeLicenseModeQuote(
    {
      annualLicenses: searchers,
      termYears: input.termYears,
      selectedModules: input.selectedModules,
      automatedWorkflows: input.automatedWorkflows,
      supportTier: input.supportTier,
      selectedIntegrations: input.selectedIntegrations,
      separateInstance: input.separateInstance,
      customIntegrationBuild: input.customIntegrationBuild,
      payment: input.payment,
    },
    config,
  )
  return {
    ...base,
    enrollment: input.enrollment,
    activationRate: input.activationRate,
    searchers,
    pricedEnrollment,
    enrollmentBand,
    onePricePerEnrolledStudentPerYear: input.enrollment > 0 ? base.annualSubscription / input.enrollment : 0,
  }
}

// ---------------------------------------------------------------------------
// Unlimited: value-based band method
// ---------------------------------------------------------------------------

export interface ValueBandResult {
  enrolled: number
  annual: number
  label: string
  custom: boolean
  onePricePerEnrolledStudentPerYear: number
}

/** Spec: all-in value-based bands, `enrolled <= max`. Reuses the same numbers as enrollment.ts PLATFORM_BANDS. */
export function computeValueBasedBandPrice(enrolled: number, config: PricingV2Config = PRICING_V2): ValueBandResult {
  const safeEnrolled = Math.max(0, enrolled)
  const band = config.unlimited.bands.find((candidate) => safeEnrolled <= candidate.max) ?? config.unlimited.bands.at(-1)!
  return {
    enrolled: safeEnrolled,
    annual: band.fee,
    label: band.label,
    custom: Boolean(band.custom),
    onePricePerEnrolledStudentPerYear: safeEnrolled > 0 ? band.fee / safeEnrolled : 0,
  }
}

// ---------------------------------------------------------------------------
// Starters
// ---------------------------------------------------------------------------

export interface StartersInput {
  programLaunch: boolean
  aiCoachingStarter: boolean
  outcomesStarter: boolean
  programLearners: number
}

export interface StarterLineItem {
  id: StarterDef['id']
  name: string
  annual: number
  draft: boolean
  note: string
  limit: number | null
  exceedsLimit: boolean
}

export interface StartersResult {
  lineItems: StarterLineItem[]
  annualSubscription: number
  anyExceedsLimit: boolean
}

export function computeStartersQuote(input: StartersInput, config: PricingV2Config = PRICING_V2): StartersResult {
  const selectedIds = new Set<StarterDef['id']>()
  if (input.programLaunch) selectedIds.add('program_launch')
  if (input.aiCoachingStarter) selectedIds.add('ai_coaching_starter')
  if (input.outcomesStarter) selectedIds.add('outcomes_starter')

  const lineItems: StarterLineItem[] = config.starters
    .filter((starter) => selectedIds.has(starter.id))
    .map((starter) => ({
      id: starter.id,
      name: starter.name,
      annual: starter.annual,
      draft: starter.draft,
      note: starter.note,
      limit: starter.limit,
      exceedsLimit: starter.limit !== null && input.programLearners > starter.limit,
    }))

  return {
    lineItems,
    annualSubscription: lineItems.reduce((sum, item) => sum + item.annual, 0),
    anyExceedsLimit: lineItems.some((item) => item.exceedsLimit),
  }
}

// ---------------------------------------------------------------------------
// Metered items (read-only reference block; see PRICING_V2.metered for rates)
// ---------------------------------------------------------------------------

/** Looks up a metered scan type's config by id. Throws if the id is unknown (should never happen for a static config). */
export function findMeteredScanType(id: MeteredScanType['id'], config: PricingV2Config = PRICING_V2): MeteredScanType {
  const found = config.metered.scanTypes.find((scanType) => scanType.id === id)
  if (!found) throw new Error(`Unknown metered scan type: ${id}`)
  return found
}

// ---------------------------------------------------------------------------
// Onboarding: three models, compared side by side
// ---------------------------------------------------------------------------

export interface OnboardingContext {
  scope: OnboardingScope
  selectedIntegrations: IntegrationId[]
  migration: boolean
  separateInstance: boolean
  /** DRAFT add-on: quantity of extra training sessions, $500 each, never waived. */
  additionalTrainingSessions: number
  manualPackage?: Option2Package | null
}

export interface OnboardingResult {
  model: OnboardingModelId
  base: number
  due: number
  breakdownLabel: string
}

/**
 * The customer-facing base-plus-add-ons model's detailed breakdown: the base
 * setup fee (full price and after any term discount/waiver) plus one line
 * per selected add-on, all of which are never discounted or waived. `base`
 * and `due` on the parent OnboardingResult remain the totals.
 */
export interface OnboardingBaseResult extends OnboardingResult {
  baseFull: number
  baseDue: number
  /** Positive amount saved by the term discount/waiver; 0 at 1 year. Shown as a negative line. */
  termDiscountAmount: number
  addOnLines: BreakdownLine[]
}

export interface OnboardingOption2Result extends OnboardingResult {
  selectedPackage: Option2Package
  autoSelectedPackage: Option2Package
}

function termDiscountDue(base: number, termYears: TermYears, twoYearDiscount: number, threeYearWaived: boolean, threeYearDiscount = 1): number {
  if (termYears === 3) return threeYearWaived ? 0 : base * (1 - threeYearDiscount)
  if (termYears === 2) return base * (1 - twoYearDiscount)
  return base
}

/**
 * Internal-view-only comparison model: max($5,000, 20% x annual subscription
 * x scope); 2yr 50% off; 3yr waived. Never drives the customer-facing quote.
 */
export function computeOnboardingCurrent(annualSubscription: number, termYears: TermYears, scope: OnboardingScope, config: PricingV2Config = PRICING_V2): OnboardingResult {
  const { floor, rate, twoYearDiscount, threeYearWaived } = config.onboarding.current
  const base = Math.max(floor, annualSubscription * rate * scope)
  const due = termDiscountDue(base, termYears, twoYearDiscount, threeYearWaived)
  return { model: 'current', base, due, breakdownLabel: `max($${floor.toLocaleString('en-US')}, 20% x annual subscription x ${scope} scope)` }
}

/**
 * The only customer-facing onboarding model: a base setup fee (roster
 * import, SSO setup, branding and custom domain, two admin trainings, and a
 * launch plan), 50% off on 2-year terms and waived on 3-year terms, plus
 * add-ons that are never discounted or waived at any term: SIS/LMS/CRM
 * integration builds, migration from an incumbent vendor, and additional
 * training sessions (DRAFT, priced per session).
 */
export function computeOnboardingBasePlusAddOns(annualSubscription: number, termYears: TermYears, context: OnboardingContext, config: PricingV2Config = PRICING_V2): OnboardingBaseResult {
  const { baseSetup, twoYearDiscount, threeYearWaived, migrationFee, additionalTrainingFee } = config.onboarding.option1
  const baseDue = termDiscountDue(baseSetup, termYears, twoYearDiscount, threeYearWaived)
  const termDiscountAmount = baseSetup - baseDue

  const addOnLines: BreakdownLine[] = []
  for (const integration of config.integrations) {
    if (context.selectedIntegrations.includes(integration.id)) {
      addOnLines.push({ label: `${integration.name} integration build`, amount: integration.onboardingFee, kind: 'charge' })
    }
  }
  if (context.migration) {
    addOnLines.push({ label: 'Migration from incumbent vendor', amount: migrationFee, kind: 'charge' })
  }
  if (context.additionalTrainingSessions > 0) {
    addOnLines.push({
      label: `Additional training (${context.additionalTrainingSessions} session${context.additionalTrainingSessions === 1 ? '' : 's'})`,
      amount: context.additionalTrainingSessions * additionalTrainingFee,
      kind: 'charge',
    })
  }
  const addOnsTotal = addOnLines.reduce((sum, line) => sum + line.amount, 0)

  return {
    model: 'option1',
    base: baseSetup + addOnsTotal,
    due: baseDue + addOnsTotal,
    baseFull: baseSetup,
    baseDue,
    termDiscountAmount,
    addOnLines,
    breakdownLabel: 'Base setup plus add-ons (integration builds, migration, and extra training never discounted)',
  }
}

/**
 * Internal-view-only comparison model: packaged tiers. Auto-select: no
 * integrations and no migration -> Standard; at most 1 integration and/or
 * migration -> Guided; 2+ integrations or a separate instance -> Enterprise.
 * Manual override allowed. Never drives the customer-facing quote.
 */
export function autoSelectOption2Package(selectedIntegrations: IntegrationId[], migration: boolean, separateInstance: boolean): Option2Package {
  if (selectedIntegrations.length >= 2 || separateInstance) return 'enterprise'
  if (selectedIntegrations.length === 0 && !migration) return 'standard'
  return 'guided'
}

export function computeOnboardingOption2(annualSubscription: number, termYears: TermYears, context: OnboardingContext, config: PricingV2Config = PRICING_V2): OnboardingOption2Result {
  const autoSelectedPackage = autoSelectOption2Package(context.selectedIntegrations, context.migration, context.separateInstance)
  const selectedPackage = context.manualPackage ?? autoSelectedPackage
  const packageConfig = config.onboarding.option2[selectedPackage]
  const due = termDiscountDue(packageConfig.price, termYears, packageConfig.twoYearDiscount, packageConfig.threeYearWaived, packageConfig.threeYearDiscount)
  return {
    model: 'option2',
    base: packageConfig.price,
    due,
    selectedPackage,
    autoSelectedPackage,
    breakdownLabel: `${selectedPackage[0]!.toUpperCase()}${selectedPackage.slice(1)} package`,
  }
}

export interface OnboardingComparison {
  current: OnboardingResult
  option1: OnboardingBaseResult
  option2: OnboardingOption2Result
}

/**
 * All three onboarding models for the same selections, for the internal
 * view's side-by-side comparison. Only `option1` (base plus add-ons) is
 * ever customer-facing; `current` and `option2` are reference-only.
 */
export function compareOnboardingModels(annualSubscription: number, termYears: TermYears, context: OnboardingContext, config: PricingV2Config = PRICING_V2): OnboardingComparison {
  return {
    current: computeOnboardingCurrent(annualSubscription, termYears, context.scope, config),
    option1: computeOnboardingBasePlusAddOns(annualSubscription, termYears, context, config),
    option2: computeOnboardingOption2(annualSubscription, termYears, context, config),
  }
}

const ZEROED_ONBOARDING_LABEL = 'No implementation fee applies; setup is included at no extra charge.'

function zeroedOnboardingComparison(): OnboardingComparison {
  return {
    current: { model: 'current', base: 0, due: 0, breakdownLabel: ZEROED_ONBOARDING_LABEL },
    option1: { model: 'option1', base: 0, due: 0, baseFull: 0, baseDue: 0, termDiscountAmount: 0, addOnLines: [], breakdownLabel: ZEROED_ONBOARDING_LABEL },
    option2: { model: 'option2', base: 0, due: 0, selectedPackage: 'standard', autoSelectedPackage: 'standard', breakdownLabel: ZEROED_ONBOARDING_LABEL },
  }
}

/**
 * Zeroes onboarding across all three models (base/due both 0, no add-on
 * lines) whenever a Starter product is applied or the quote is a renewal.
 * Both cases mean setup carries no separate onboarding fee, so the summary,
 * the payment schedule, and the internal view's comparison all agree there
 * is nothing to discount or waive. `zeroed` is computed by the caller (e.g.
 * `anyStarterApplied || renewalQuote`), since that's a UI-input concern, not
 * a pricing rule this module owns.
 */
export function computeOnboardingForMode(zeroed: boolean, annualSubscription: number, termYears: TermYears, context: OnboardingContext, config: PricingV2Config = PRICING_V2): OnboardingComparison {
  if (!zeroed) return compareOnboardingModels(annualSubscription, termYears, context, config)
  return zeroedOnboardingComparison()
}

// ---------------------------------------------------------------------------
// Payment schedule (baseline: mirrors the live license.ts/enrollment.ts on
// main, which bills the full contract at signature for every term)
// ---------------------------------------------------------------------------

export interface PaymentScheduleResult {
  paymentSchedule: PaymentLine[]
  totalContractValue: number
  platformTerm: number
}

/**
 * Every term (1, 2, or 3 years) bills the full contract, onboarding
 * included, at signature. There is no day-90 split. The onboarding amount
 * is also broken out as a separate `included: true` detail line (a
 * component of the signature total, not an additional charge) so the UI can
 * show the discount/waiver detail without double-counting it; callers
 * should filter `included` lines out of the rendered "payment schedule"
 * list, matching the live calculator's convention. The 90-day opt-out
 * always refunds the prepaid subscription and keeps only the onboarding fee
 * actually paid.
 *
 * `includeOnboarding` (default true) lets Starters and renewal quotes drop
 * onboarding entirely: no onboarding line, and it never contributes to the
 * total contract value, because setup is included at no extra charge.
 *
 * `payment` (default 'upfront') switches a 2- or 3-year term to pay one year at a time instead of
 * the full term at signature (Rod, 2026-09-27): at signature is Year 1's subscription plus
 * implementation, then each remaining year's subscription falls due on its anniversary. The total
 * contract value is unchanged either way — it is still the sum of every year's subscription plus
 * implementation — only how it is split into payments changes. A 1-year term always behaves as
 * upfront regardless of this flag, since there is only ever one payment. The 90-day opt-out
 * refunds the prepaid Year 1 subscription and keeps only implementation fees already paid.
 */
export function computePaymentSchedule(
  annualSubscription: number,
  termYears: TermYears,
  onboardingDue: number,
  onboardingBase: number,
  includeOnboarding: boolean = true,
  payment: PaymentFrequency = 'upfront',
): PaymentScheduleResult {
  const platformTerm = annualSubscription * termYears
  const effectiveOnboardingDue = includeOnboarding ? onboardingDue : 0
  const totalContractValue = platformTerm + effectiveOnboardingDue
  const effectivePayment: PaymentFrequency = termYears === 1 ? 'upfront' : payment

  const paymentSchedule: PaymentLine[] = []

  if (effectivePayment === 'yearly') {
    paymentSchedule.push({ label: 'At signature', timing: 'Year 1 subscription plus implementation', amount: annualSubscription + effectiveOnboardingDue })
  } else {
    paymentSchedule.push({ label: 'At signature', timing: `Full ${termYears}-year contract`, amount: totalContractValue })
  }

  if (includeOnboarding) {
    paymentSchedule.push({
      label: 'Implementation fee',
      timing: termYears === 3 ? 'Waived with a 3-year term' : termYears === 2 ? '50% off with a 2-year term' : 'One-time setup and launch',
      amount: onboardingDue,
      ...(onboardingDue !== onboardingBase ? { originalAmount: onboardingBase } : {}),
      included: true,
    })
  }

  if (effectivePayment === 'yearly') {
    if (termYears >= 2) paymentSchedule.push({ label: 'Year 2', timing: 'Due on your first anniversary', amount: annualSubscription })
    if (termYears >= 3) paymentSchedule.push({ label: 'Year 3', timing: 'Due on your second anniversary', amount: annualSubscription })
  }

  paymentSchedule.push({
    label: '90-day opt-out',
    timing: effectivePayment === 'yearly'
      ? (includeOnboarding
          ? 'The prepaid Year 1 subscription is refunded. Implementation fees already paid are kept.'
          : 'The prepaid Year 1 subscription is refunded.')
      : (includeOnboarding
          ? 'The prepaid annual subscription is refunded. Implementation fees already paid are kept.'
          : 'The prepaid annual subscription is refunded.'),
    amount: effectiveOnboardingDue,
    retained: true,
  })

  return { paymentSchedule, totalContractValue, platformTerm }
}

// ---------------------------------------------------------------------------
// Switching credit (internal view only, DRAFT policy)
// ---------------------------------------------------------------------------

export interface SwitchingCreditInput {
  remainingObligation: number
  termYears: TermYears
  threeYearSubscriptionGross: number
  capPercent?: number
}

export interface SwitchingCreditResult {
  eligible: boolean
  cap: number
  creditApplied: number
}

/** Applied against year one, only on 3-year terms, capped at capPercent of the 3-year subscription gross. */
export function computeSwitchingCredit(input: SwitchingCreditInput, config: PricingV2Config = PRICING_V2): SwitchingCreditResult {
  const eligible = input.termYears === 3
  const capPercent = input.capPercent ?? config.switchingCredit.capPercent
  const cap = input.threeYearSubscriptionGross * capPercent
  const creditApplied = eligible ? Math.min(Math.max(0, input.remainingObligation), cap) : 0
  return { eligible, cap, creditApplied }
}

/** Deducts an eligible switching credit from the contract total, shown as a negative line in the summary. Never goes below 0. */
export function applySwitchingCredit(totalContractValue: number, creditApplied: number): number {
  return Math.max(0, totalContractValue - Math.max(0, creditApplied))
}

// ---------------------------------------------------------------------------
// The "one number"
// ---------------------------------------------------------------------------

export interface OneNumberResult {
  perLicensePerYear: number
  perEnrolledStudentPerYear?: number
}

/** Effective all-in price per license (or searcher) per year; per enrolled student too when enrollment is known. */
export function computeOneNumber(annualSubscription: number, billedLicensesOrSearchers: number, enrolled?: number): OneNumberResult {
  const perLicensePerYear = billedLicensesOrSearchers > 0 ? annualSubscription / billedLicensesOrSearchers : 0
  if (enrolled !== undefined && enrolled > 0) {
    return { perLicensePerYear, perEnrolledStudentPerYear: annualSubscription / enrolled }
  }
  return { perLicensePerYear }
}

/**
 * The right panel's "Per license" figure (round 6 spec, never "per year"):
 * the subscription total for the WHOLE term divided by pooled licenses, not
 * an annual rate. Example: 1,000 licenses/year, 3-year term, Full Platform
 * -> $81,000 subscription over the term / 3,000 pooled licenses = $27.00.
 */
export function computePerLicenseOverTerm(subscriptionTotalForTerm: number, pooledLicenses: number): number {
  return pooledLicenses > 0 ? subscriptionTotalForTerm / pooledLicenses : 0
}

// ---------------------------------------------------------------------------
// Unified input shape + defaults, for the React component
// ---------------------------------------------------------------------------

export interface PricingV2Assumptions {
  activationRate: number
  fullPlatformDiscountBase: FullPlatformDiscountBase
}

export interface PricingV2Input {
  enrollment: number
  termYears: TermYears
  /** Only meaningful for 2- and 3-year terms; a 1-year term always behaves as 'upfront' regardless of this value (Rod, 2026-09-27). */
  payment: PaymentFrequency
  /** Top-level segmented toggle: only these two customer-facing modes. Starter products are an overlay, not a mode. */
  mode: PricingMode
  /** Drives recommendation copy only; no price effect. */
  customerType: CustomerType
  annualLicenses: number
  selectedModules: ModuleId[]
  automatedWorkflows: boolean
  careerExplorationInterest: boolean
  supportTier: SupportTier
  selectedIntegrations: IntegrationId[]
  migrationFromIncumbent: boolean
  separateInstance: boolean
  /** Custom connectors beyond the standard SIS/LMS/CRM, scoped in a statement of work. Forces Enterprise support. */
  customIntegrationBuild: boolean
  /** Overlay: when any of these is true, the Starters quote replaces the license/unlimited headline regardless of `mode`. */
  starters: StartersInput
  onboardingScope: OnboardingScope
  /** DRAFT add-on: quantity of extra training sessions in the base-plus-add-ons onboarding model. */
  additionalTrainingSessions: number
  /** Internal-view-only comparison knob for the Option 2 packages reference card. */
  onboardingManualPackage: Option2Package | null
  /** Internal-view-only: when true, onboarding is $0 and the summary reads "Renewal, no onboarding fee." */
  renewalQuote: boolean
  switchingCredit: { remainingObligation: number; capPercent: number }
  assumptions: PricingV2Assumptions
}

/** Realistic example defaults used by /pricing-lab: 1,000 licenses, 1 year, Full Platform, 10,000 enrollment. */
export const DEFAULT_PRICING_V2_INPUT: PricingV2Input = {
  enrollment: 10_000,
  termYears: 1,
  payment: 'upfront',
  mode: 'license',
  customerType: 'career_program',
  annualLicenses: 1_000,
  selectedModules: ['ai_coaching', 'employer_partnerships', 'outcomes_pro'],
  automatedWorkflows: false,
  careerExplorationInterest: false,
  supportTier: 'professional',
  selectedIntegrations: [],
  migrationFromIncumbent: false,
  separateInstance: false,
  customIntegrationBuild: false,
  starters: { programLaunch: false, aiCoachingStarter: false, outcomesStarter: false, programLearners: 100 },
  onboardingScope: 1,
  additionalTrainingSessions: 0,
  onboardingManualPackage: null,
  renewalQuote: false,
  switchingCredit: { remainingObligation: 0, capPercent: 0.25 },
  assumptions: { activationRate: 0.25, fullPlatformDiscountBase: 'subscription' },
}

// ---------------------------------------------------------------------------
// v1 <-> v2 module mapping and the saved-input adapter, so a workspace that
// saved calculator_inputs under the old license.ts/enrollment.ts shape (or
// has none yet) still loads sensibly into the v2 UI. See
// src/components/WorkspaceExperience.tsx (loads pricing) and
// worker/routes/workspace-quotes.ts (recomputes quotes server-side).
// ---------------------------------------------------------------------------

type V1ModuleId = 'matching' | 'employer' | 'outcomes' | 'agent'

/**
 * v1 license.ts module ids mapped to their closest v2 module. 'matching'
 * (Job Matching + Insights) has no v2 equivalent — daily job matching moved
 * into the base included features in v2 — so it maps to null and is simply
 * dropped by the adapter below.
 */
export const V1_TO_V2_MODULE: Record<V1ModuleId, ModuleId | null> = {
  matching: null,
  employer: 'employer_partnerships',
  outcomes: 'outcomes_pro',
  agent: 'ai_coaching',
}

/**
 * Inverse of V1_TO_V2_MODULE. Used only to shape the renderer payload the
 * Worker sends to internal-agent's v1-only quote PDF generator (see
 * worker/routes/workspace-quotes.ts) — never used for the real saved
 * workspace calculator input, which stays natively v2-shaped.
 */
export const V2_TO_V1_MODULE: Record<ModuleId, V1ModuleId> = {
  ai_coaching: 'agent',
  employer_partnerships: 'employer',
  outcomes_pro: 'outcomes',
}

/** Starting v2 mode for a workspace's pricing model: license -> Per license, enrollment_roi -> Unlimited. */
export function pricingV2ModeForPricingModel(pricingModel: 'enrollment_roi' | 'license'): PricingMode {
  return pricingModel === 'license' ? 'license' : 'unlimited'
}

/**
 * True when a saved calculatorInputs value is v2-shaped. Used both by the adapter above and by
 * WorkspaceExperience.tsx's value-opportunity snapshot, which must NOT spread v2 fields (e.g. v2's
 * `selectedModules` uses different ids than v1's LICENSE_MODULES) into the v1
 * LicensePricingInput/EnrollmentRoiInput defaults it still uses for that separate shared-value
 * model — doing so would silently corrupt fields like selectedModules rather than fail loudly.
 */
export function isPricingV2SavedInput(value: unknown): value is Record<string, unknown> & { formulaVersion: string } {
  return Boolean(value) && typeof value === 'object' && (value as Record<string, unknown>).formulaVersion === PRICING_V2_INPUT_VERSION
}

function withoutFormulaVersion(record: Record<string, unknown>): Partial<PricingV2Input> {
  const { formulaVersion: _drop, ...rest } = record
  return rest as Partial<PricingV2Input>
}

/**
 * Adapts a workspace's saved calculatorInputs into a full PricingV2Input the
 * v2 UI and the Worker's quote route both use. Three cases:
 *  - Nothing saved yet: v2 defaults, starting in the mode the pricing model implies.
 *  - Already v2-shaped (tagged with PRICING_V2_INPUT_VERSION on `formulaVersion`):
 *    merges over the v2 defaults, same as the component's own useState initializer.
 *  - v1-shaped (license.ts/enrollment.ts input): carries over only the fields that
 *    map cleanly — annualLicenses/enrolled, termYears, automatedWorkflows, supportTier,
 *    and modules where V1_TO_V2_MODULE has an equivalent — everything else falls back
 *    to the v2 default for the mapped starting mode.
 */
export function adaptSavedInputToPricingV2(pricingModel: 'enrollment_roi' | 'license', saved: unknown): PricingV2Input {
  const mode = pricingV2ModeForPricingModel(pricingModel)
  const base: PricingV2Input = { ...DEFAULT_PRICING_V2_INPUT, mode }
  if (!saved || typeof saved !== 'object') return base
  if (isPricingV2SavedInput(saved)) return { ...base, ...withoutFormulaVersion(saved as Record<string, unknown>) }
  const record = saved as Record<string, unknown>

  const result: PricingV2Input = { ...base }
  if (record.termYears === 1 || record.termYears === 2 || record.termYears === 3) result.termYears = record.termYears
  if (typeof record.automatedWorkflows === 'boolean') result.automatedWorkflows = record.automatedWorkflows
  if (typeof record.supportTier === 'string' && ['standard', 'professional', 'enterprise'].includes(record.supportTier)) {
    result.supportTier = record.supportTier as SupportTier
  }

  if (mode === 'license') {
    if (typeof record.annualLicenses === 'number' && record.annualLicenses > 0) result.annualLicenses = Math.floor(record.annualLicenses)
  } else if (typeof record.enrolled === 'number' && record.enrolled > 0) {
    result.enrollment = Math.floor(record.enrolled)
  }

  if (Array.isArray(record.selectedModules)) {
    const mapped = record.selectedModules
      .map((id) => (typeof id === 'string' ? V1_TO_V2_MODULE[id as V1ModuleId] : undefined))
      .filter((id): id is ModuleId => Boolean(id))
    if (mapped.length > 0) result.selectedModules = Array.from(new Set(mapped))
  }

  return result
}

/** Builds a PricingV2Config with the internal view's editable assumptions merged in. */
export function buildConfigWithAssumptions(assumptions: PricingV2Assumptions, base: PricingV2Config = PRICING_V2): PricingV2Config {
  return {
    ...base,
    fullPlatform: { ...base.fullPlatform, discountBase: assumptions.fullPlatformDiscountBase },
  }
}

// ---------------------------------------------------------------------------
// Full-quote orchestration: the numeric result both PricingCalculatorV2 (the
// React UI) and the Worker's quote-generation route need. Kept as one
// function so the Worker's server-side recompute can never drift from what
// the UI showed the customer — both call sites run this exact sequence. It
// intentionally stops short of the UI's presentational BreakdownLine[]
// arrays (subscriptionBreakdownLines/onboardingBreakdownLines): those are
// display-only label/detail strings the component still builds itself, not
// numbers the Worker's quote payload needs.
// ---------------------------------------------------------------------------

export interface PricingV2Headline {
  annualSubscription: number
  billedUnits: number
  enrolled: number | undefined
  label: string
}

export interface PricingV2FullQuote {
  licenseQuote: LicenseModeResult
  unlimitedFormula: UnlimitedFormulaResult
  startersQuote: StartersResult
  /** True whenever a Starter product (Program Launch, etc.) overlays the license/unlimited headline. */
  anyStarterApplied: boolean
  /** True when onboarding is zeroed out entirely (a Starter product or an internal-view renewal quote). */
  isZeroedOnboarding: boolean
  /** licenseQuote or unlimitedFormula, whichever `input.mode` selects. */
  activeQuote: LicenseModeResult | UnlimitedFormulaResult
  headline: PricingV2Headline
  /** The only customer-facing onboarding model (base setup plus add-ons). */
  selectedOnboarding: OnboardingBaseResult
  paymentSchedule: PaymentLine[]
  totalContractValue: number
  platformTerm: number
  oneNumber: OneNumberResult
  perLicenseOverTerm: number
}

/**
 * Fixed rules a host page can put on the calculator (the partner Pricing
 * section uses them). `lockedModules` are always selected. With
 * `includeImplementation: false` the quote leaves implementation out, the
 * same way a renewal quote does, so no total includes a fee the page hides.
 */
export interface CalculatorRules {
  lockedModules?: ModuleId[]
  includeImplementation?: boolean
  /** The modes the calculator may use. A mode outside this list falls back to the first one. Omitted means both. */
  allowedModes?: PricingMode[]
}

export function applyCalculatorRules(input: PricingV2Input, rules: CalculatorRules = {}): PricingV2Input {
  const locked = rules.lockedModules ?? []
  const selectedModules = locked.every((id) => input.selectedModules.includes(id)) ? input.selectedModules : Array.from(new Set([...input.selectedModules, ...locked]))
  const allowedModes = rules.allowedModes?.length ? rules.allowedModes : null
  return {
    ...input,
    selectedModules,
    ...(allowedModes && !allowedModes.includes(input.mode) ? { mode: allowedModes[0]! } : {}),
    ...(rules.includeImplementation === false ? { renewalQuote: true } : {}),
  }
}

// ---------------------------------------------------------------------------
// Pricing modes per workspace (Rod, 2026-10-26): Unlimited is an option for
// universities only. The admin setting overrides the automatic rule. The
// customer calculator and the Worker's quote route both use this function.
// ---------------------------------------------------------------------------

export const PRICING_MODES_SETTINGS: PricingModesSetting[] = ['auto', 'license_only', 'license_and_unlimited']

export function normalizePricingModesSetting(value: unknown): PricingModesSetting {
  return PRICING_MODES_SETTINGS.includes(value as PricingModesSetting) ? value as PricingModesSetting : 'auto'
}

/** The customer type saved with the workspace's calculator inputs, when there is one. */
export function savedCustomerType(calculatorInputs: unknown): CustomerType | null {
  if (!calculatorInputs || typeof calculatorInputs !== 'object' || Array.isArray(calculatorInputs)) return null
  const value = (calculatorInputs as Record<string, unknown>).customerType
  return value === 'university' || value === 'career_program' ? value : null
}

/**
 * The pricing modes a workspace's customer calculator offers, in display order.
 * 'auto' offers Unlimited only when the customer logo set is University or the
 * saved calculator customer type is University. Pass the saved workspace
 * inputs, never a request body, so a client cannot unlock Unlimited.
 */
export function allowedPricingModes(setting: unknown, context: { customerSegment: CustomerSegment | string | null | undefined; calculatorInputs: unknown }): PricingMode[] {
  const normalized = normalizePricingModesSetting(setting)
  if (normalized === 'license_only') return ['license']
  if (normalized === 'license_and_unlimited') return ['license', 'unlimited']
  const university = context.customerSegment === 'university' || savedCustomerType(context.calculatorInputs) === 'university'
  return university ? ['license', 'unlimited'] : ['license']
}

export function computeFullPricingV2Quote(input: PricingV2Input, config: PricingV2Config = PRICING_V2): PricingV2FullQuote {
  const licenseQuote = computeLicenseModeQuote(
    {
      annualLicenses: input.annualLicenses,
      termYears: input.termYears,
      selectedModules: input.selectedModules,
      automatedWorkflows: input.automatedWorkflows,
      supportTier: input.supportTier,
      selectedIntegrations: input.selectedIntegrations,
      separateInstance: input.separateInstance,
      customIntegrationBuild: input.customIntegrationBuild,
      payment: input.payment,
    },
    config,
  )
  const unlimitedFormula = computeUnlimitedFormulaQuote(
    {
      enrollment: input.enrollment,
      activationRate: input.assumptions.activationRate,
      termYears: input.termYears,
      selectedModules: input.selectedModules,
      automatedWorkflows: input.automatedWorkflows,
      supportTier: input.supportTier,
      selectedIntegrations: input.selectedIntegrations,
      separateInstance: input.separateInstance,
      customIntegrationBuild: input.customIntegrationBuild,
      payment: input.payment,
    },
    config,
  )
  const startersQuote = computeStartersQuote(input.starters, config)

  const anyStarterApplied = input.starters.programLaunch || input.starters.aiCoachingStarter || input.starters.outcomesStarter
  const isZeroedOnboarding = anyStarterApplied || input.renewalQuote
  const activeQuote = input.mode === 'license' ? licenseQuote : unlimitedFormula

  const headline: PricingV2Headline = anyStarterApplied
    ? { annualSubscription: startersQuote.annualSubscription, billedUnits: 0, enrolled: undefined, label: 'flat annual fee' }
    : input.mode === 'license'
      ? { annualSubscription: licenseQuote.annualSubscription, billedUnits: licenseQuote.billedLicenses, enrolled: undefined, label: 'per license' }
      : { annualSubscription: unlimitedFormula.annualSubscription, billedUnits: unlimitedFormula.searchers, enrolled: input.enrollment, label: 'per license' }

  const onboardingContext: OnboardingContext = {
    scope: input.onboardingScope,
    selectedIntegrations: input.selectedIntegrations,
    migration: input.migrationFromIncumbent,
    separateInstance: input.separateInstance,
    additionalTrainingSessions: input.additionalTrainingSessions,
    manualPackage: input.onboardingManualPackage,
  }
  const onboardingComparison = computeOnboardingForMode(isZeroedOnboarding, headline.annualSubscription, input.termYears, onboardingContext, config)
  const selectedOnboarding = onboardingComparison.option1

  const { paymentSchedule, totalContractValue, platformTerm } = computePaymentSchedule(
    headline.annualSubscription,
    input.termYears,
    selectedOnboarding.due,
    selectedOnboarding.base,
    !isZeroedOnboarding,
    input.payment,
  )

  const oneNumber = computeOneNumber(headline.annualSubscription, headline.billedUnits, headline.enrolled)
  const perLicenseOverTerm = computePerLicenseOverTerm(platformTerm, licenseQuote.pooledLicenses)

  return {
    licenseQuote,
    unlimitedFormula,
    startersQuote,
    anyStarterApplied,
    isZeroedOnboarding,
    activeQuote,
    headline,
    selectedOnboarding,
    paymentSchedule,
    totalContractValue,
    platformTerm,
    oneNumber,
    perLicenseOverTerm,
  }
}
